/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import * as React from 'react';
import { dataText } from './vineyard-map.data-map';
import { IVineyardMapConfig } from './vineyard-map.props.autogenerated';

// =============================================================================
// INTERFACES
// =============================================================================

interface IVineyardMapTextProps {
    activeArea: number;
    config: IVineyardMapConfig;
    context: ICoreContext;
}

interface IDataText {
    area: number;
    heading: string;
    text: string;
}

/**
 *
 * VineyardMapText component: Renders the name and corresponding description of
 * the currently active vineyard area
 * @extends {React.Component<IVineyardMapTextProps>}
 */

class VineyardMapText extends React.Component<IVineyardMapTextProps> {

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================

    public render(): JSX.Element {
        const config = this.props.config;
        const lazyloadConfig: boolean | undefined = (config && config.lazyload) || false;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
            },
            lazyload: lazyloadConfig
        };

        return (
            <div className='vineyard-map-sidebar'>
                {
                    config.logoImage && (
                        <div className='vineyard-map-logo'>
                            <Image
                                {...config.logoImage}
                                gridSettings={this.props.context.request.gridSettings!}
                                imageSettings={config.logoImage.imageSettings || defaultImageSettings}
                            />
                        </div>
                    )
                }
                {
                    config.headerImage && (
                        <div className='vineyard-map-title'>
                            <Image
                                {...config.headerImage}
                                gridSettings={this.props.context.request.gridSettings!}
                                imageSettings={config.headerImage.imageSettings || defaultImageSettings}
                            />
                        </div>
                    )
                }
                <div className='vineyard-map-content'>
                    <h3 className='vineyard-map-heading'>
                        {this._renderActiveText().heading}
                    </h3>
                    <p className='vineyard-map-text'>
                        {this._renderActiveText().text}
                    </p>
                </div>
            </div>
        );
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    /**
     * obtains data of active vineyard area to render text, returns an object
     */
    private _renderActiveText(): IDataText {
        return dataText.find((shape: IDataText) => shape.area === this.props.activeArea) as IDataText;
    }
}

export default VineyardMapText;
